import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeArrayTransformer, getDatabaseCodeTransformer} from "./transfomers/transformers";
import {NoticeKind, toNoticeKind} from "./notice-kind";
import {NoticeCategory, toNoticeCategory} from "./notice-category";
import {NoticeSeverity, toNoticeSeverity} from "./notice-severity";
import {FieldMatch} from "./search/search";
import {SortFieldSpec} from "./search/sort-field-spec";
import {PageSpec} from "./search/page-spec";
import {adjustFromTimeZoneSafely} from "../date/date";
import {PersonCapabilityType, toPersonCapabilityType} from "./person-capability-type";

export const NoticeCodec = t.intersection([
  t.type({
    id: t.string,
    kind: t.string,
    category: t.string,
    severity: t.string,
    title: t.string,
    bodyText: t.string,
  }),
  t.partial({
    bodyHtml: t.string,
    expiresDttm: t.number,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    createdByName: t.string,

    scopedRegionIds: t.array(t.string),
    scopedRegionNames: t.array(t.string),
    scopedCompanyIds: t.array(t.string),
    scopedCompanyNames: t.array(t.string),
    scopedAudienceCapabilities: t.array(t.string),
  }),
]);

export type Notice = t.TypeOf<typeof NoticeCodec>;

@Exclude()
export class NoticeEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNoticeKind))
  kind!: NoticeKind;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNoticeCategory))
  category!: NoticeCategory;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNoticeSeverity))
  severity!: NoticeSeverity;

  @Expose()
  title!: string;

  @Expose()
  bodyText!: string;

  @Expose()
  bodyHtml?: string;

  @Expose()
  expiresDttm?: number;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  createdByName?: string;

  @Expose()
  scopedRegionIds?: string[];

  @Expose()
  scopedRegionNames?: string[];

  @Expose()
  scopedCompanyIds?: string[];

  @Expose()
  scopedCompanyNames?: string[];

  @Expose()
  @Transform(getDatabaseCodeArrayTransformer(toPersonCapabilityType))
  scopedAudienceCapabilities?: PersonCapabilityType[];

  readonly entityType = "NOTICE";

  get displayName(): Readonly<string> {
    return this.title ?? this.id;
  }

  constructor() {
  }

  static from(value: Notice): NoticeEntity {
    return plainToInstance(NoticeEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Notice> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(NoticeCodec, object);
  }

  asTypeValue(): Notice {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(NoticeCodec, object);
  }

  isExpired = (dttm: number = new Date().getTime()): boolean => {
    const adjustedExpiredDttm = adjustFromTimeZoneSafely(this.expiresDttm)?.getTime()
    return typeof adjustedExpiredDttm !== "undefined" && adjustedExpiredDttm <= dttm
  }
}

export interface NoticeSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  dttmField?: string;
  ids?: string[]
  region?: FieldMatch;
  company?: FieldMatch;
  audienceCapability?: FieldMatch;
  createdBy?: FieldMatch;
  lastModifiedBy?: FieldMatch;
  kinds?: string[]
  categories?: string[]
  severities?: string[]
  sort?: SortFieldSpec[]
  page?: PageSpec
}

export interface NoticeCreateRequestDTO {
  kind: string;
  category: string;
  severity: string;
  title: string;
  bodyText: string;
  bodyHtml?: string;
  expiresDttm?: number;
  regionIds?: string[];
  companyIds?: string[];
  audienceCapabilities?: string[];
}

export interface NoticeUpdateExpirationRequestDTO {
  expiresDttm?: number;
}

export const NoticeBadgeSummaryCodec = t.intersection([
  t.type({
    activeCount: t.number,
  }),
  t.partial({}),
]);

export type NoticeBadgeSummary = t.TypeOf<typeof NoticeBadgeSummaryCodec>;
